// Shared types for YouPass Admin PWA

export enum UserRole {
  CLIENT = 'client',
  ADMIN = 'admin',
}

export enum BookletMode {
  FREE = 'free',
  SURPRISE = 'surprise',
  SEQUENTIAL = 'sequential',
}

// Tipo di prodotto: normale, custom o flashcard
export enum ProductType {
  NORMAL = 'normal', // Prodotto standard con voucher predefiniti (attivazione e riscatto)
  CUSTOM = 'custom', // Prodotto custom con prezzi configurabili per taglio
  FLASHCARD = 'flashcard', // Card da visualizzare in guest (no attivazione): scoperta o step
}

// Modalità visualizzazione flashcard in guest (solo per productType === FLASHCARD)
export enum FlashcardDisplayMode {
  DISCOVERY = 'discovery', // A scoperta: tutte le card visibili
  STEP = 'step', // A step: una card alla volta, prima title poi cliccando description, freccia avanti
}

// Tipo di blocchetto: normale (da prodotto) o custom
export enum BookletType {
  NORMAL = 'normal',
  CUSTOM = 'custom',
}

// Tipo contenuto: standard (visibile) o mystery (nascosto)
export enum ContentType {
  STANDARD = 'standard',
  MYSTERY = 'mystery',
}

// Modalità di riscatto
export enum RedemptionMode {
  SELF_REDEEM = 'self-redeem',
  ACTION_REQUIRED = 'action-required',
}

// Modalità di scoperta (discovery mode)
export enum DiscoveryMode {
  FIXED_ORDER = 'fixed-order',
  FREE_SHUFFLE = 'free-shuffle',
  FINAL_SHUFFLE = 'final-shuffle',
  PROGRESSIVE = 'progressive',
}

// Sottotipo per sblocco progressivo
export enum ProgressiveSubtype {
  DAILY = 'daily',
  WEEKLY = 'weekly',
}

export enum VoucherStatus {
  LOCKED = 'locked',
  AVAILABLE = 'available',
  REDEEMED = 'redeemed',
}

export enum OrderStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  createdAt: string;
}

export interface User {
  _id: string;
  email: string;
  role: UserRole;
  isEmailVerified: boolean;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  devices?: Device[];
  pushSubscriptions?: PushSubscription[];
  createdAt: string;
  updatedAt: string;
}

export interface Device {
  fingerprint: string;
  userAgent: string;
  ip: string;
  isApproved: boolean;
  approvedAt?: string;
  lastUsedAt: string;
}

export type ProductCategory = string; // Category slug from database

export type ProductStatus = 'bozza' | 'confermato' | 'pubblicato' | 'non-disponibile';

export interface Product {
  _id: string;
  name: string | { it: string; en: string; es: string; fr: string }; // Multilingual
  slug: string; // URL-friendly identifier
  subtitle?: string | { it: string; en: string; es: string; fr: string }; // Multilingual
  description: string | { it: string; en: string; es: string; fr: string }; // Multilingual
  marketingLabel: string | { it: string; en: string; es: string; fr: string }; // Multilingual
  /** Lingue abilitate per questo prodotto (default: it, en, es, fr) */
  enabledLanguages?: ('it' | 'en' | 'es' | 'fr')[];
  category: ProductCategory;
  productType: ProductType; // NORMAL, CUSTOM o FLASHCARD
  totalVouchers?: number; // Solo per prodotti NORMAL
  price?: number; // Solo per prodotti NORMAL
  flashcardDisplayMode?: FlashcardDisplayMode; // Solo per FLASHCARD
  discount?: {
    type: 'percentage' | 'fixed' | 'none';
    value: number;
  };
  currency: string;
  theme: Theme;
  coverImage: string;
  vouchers?: ProductVoucher[]; // NORMAL e FLASHCARD (card)
  isActive: boolean;
  isFeatured?: boolean; // Prodotto in evidenza nella home page
  status: ProductStatus; // Stato del prodotto: bozza, pubblicato, non-disponibile
  // Default configuration for giftboxes created from this product (solo per NORMAL)
  defaultContentType?: ContentType;
  defaultRedemptionMode?: RedemptionMode;
  defaultDiscoveryMode?: DiscoveryMode | DiscoveryMode[]; // Può essere multipla
  defaultProgressiveConfig?: ProgressiveConfig;
  // Prezzi per taglio (solo per prodotti CUSTOM)
  priceByVoucherCount?: Record<number, number>; // Es: { 5: 50, 10: 90, 15: 130, 20: 170 }
  createdAt: string;
  updatedAt: string;
}

export interface Theme {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  pattern?: string;
  fontFamily?: string;
  // Booklet customization
  bookletTitle?: string;
  bookletSubtitle?: string;
  bookletBrandName?: string;
  bookletTagline?: string;
  bookletInstructions?: string;
  bookletGreeting?: string;
  bookletDecorationStyle?: 'hearts' | 'stars' | 'flowers' | 'minimal';
  // Terms and conditions template (multilingual)
  termsAndConditionsTemplate?: string | { it: string; en: string; es: string; fr: string };
  // Voucher back fields labels
  voucherBackLabels?: {
    date?: string;
    for?: string;
    from?: string;
    signature1?: string;
    signature2?: string;
  };
}

export interface ProductVoucher {
  nr: number;
  title: string | { it: string; en: string; es: string; fr: string }; // Multilingual
  description: string | { it: string; en: string; es: string; fr: string }; // Multilingual
  termsAndConditions?: string | { it: string; en: string; es: string; fr: string }; // Multilingual
  backgroundImage?: string;
  customCode?: string;
  occasion?: string | { it: string; en: string; es: string; fr: string }; // Multilingual
}

export interface GiftBox {
  _id: string;
  productId?: string | Product; // Opzionale per blocchetti custom
  ownerId?: string | User; // Can be null for guest orders, or populated User object
  orderId: string | Order; // Can be string ID or populated Order object
  // Nuovi campi
  bookletType: BookletType;
  contentType: ContentType;
  redemptionMode: RedemptionMode;
  availableDiscoveryModes: DiscoveryMode[]; // Array di modalità disponibili dal prodotto
  discoveryMode?: DiscoveryMode | null; // Modalità scelta all'attivazione (null se multiple disponibili)
  customConfig?: CustomBookletConfig;
  progressiveConfig?: ProgressiveConfig;
  shuffleConfig?: ShuffleConfig;
  // Campi legacy (mantenuti per retrocompatibilità)
  mode: BookletMode;
  sequentialConfig?: SequentialConfig;
  // Altri campi
  isActive: boolean;
  lockedConfig: boolean;
  activationLink?: string;
  activationToken?: string;
  partnerToken?: string;
  partnerNotified: boolean;
  vouchers: GiftBoxVoucher[];
  metadata: GiftBoxMetadata;
  createdAt: string;
  updatedAt: string;
}

export interface GiftBoxVoucher {
  nr: number;
  title: string;
  description: string;
  termsAndConditions?: string;
  backgroundImage?: string;
  status: VoucherStatus;
  redeemedAt?: string;
  redeemedBy?: string;
  unlockDate?: string;
}

export interface SequentialConfig {
  startDate: string;
  interval: 'daily' | 'weekly' | 'monthly';
  intervalCount: number;
}

// Configurazione per blocchetti custom
export interface CustomBookletConfig {
  totalVouchers: number; // 5, 10, o 15
  useAI: boolean;
  aiPrompt?: string;
}

// Configurazione per discovery mode progressivo
export interface ProgressiveConfig {
  subtype: ProgressiveSubtype;
  startDate: string;
  intervalCount?: number;
}

// Configurazione per discovery mode shuffle
export interface ShuffleConfig {
  shuffled: boolean;
  shuffledAt?: string;
  shuffleCount?: number;
}

export interface GiftBoxMetadata {
  totalVouchers: number;
  redeemedVouchers: number;
  availableVouchers: number;
  lastRedeemedAt?: string;
  firstRedeemedAt?: string;
}

export interface Order {
  _id: string;
  orderNumber: string;
  userId?: string;
  guestEmail?: string;
  productId: string | Product;
  quantity: number;
  totalAmount: number;
  currency: string;
  status: OrderStatus;
  receiptPdfUrl?: string;
  giftBoxIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: {
    items: T[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}
